/**
 * Multi-tenant resolution hook.
 * 
 * Resolves the current tenant (artist) from domain or session (auth state).
 * 
 * Resolution modes (configured via VITE_TENANT_RESOLUTION):
 *   'domain'  — extracts subdomain from Host header using BASE_DOMAIN
 *   'session' — tenant resolved from authenticated user's session (no URL path prefix)
 * 
 * Both modes also support ?tenant= query param for dev/preview convenience.
 */

import { env } from '../config/env';

// --- Admin-as-tenant context helpers ---
const ADMIN_AS_KEY = 'admin-as-tenant';

/** Get the admin impersonation slug from sessionStorage (if active). */
export function getAdminAsSlug(): string | null {
  try { return sessionStorage.getItem(ADMIN_AS_KEY); } catch { return null; }
}

/** Persist admin impersonation slug into sessionStorage. */
export function setAdminAsSlug(slug: string): void {
  try { sessionStorage.setItem(ADMIN_AS_KEY, slug); } catch {}
}

/** Clear admin impersonation context (e.g. when returning to /admin). */
export function clearAdminAsSlug(): void {
  try { sessionStorage.removeItem(ADMIN_AS_KEY); } catch {}
}

export interface TenantInfo {
  /** The raw subdomain slug (e.g., "artist-name") */
  slug: string;
  /** Whether this is a valid tenant route */
  isTenant: true;
}

export interface NoTenant {
  slug: null;
  isTenant: false;
}

export type TenantResult = TenantInfo | NoTenant;

// Subdomains to ignore (system-level)
const RESERVED_SUBDOMAINS = ['www', 'api', 'admin', 'app', 'mail', 'smtp', 'cdn', 'static'];

/**
 * Get the configured base domain from env (no hardcoded constants).
 */
export function getBaseDomain(): string {
  return env.BASE_DOMAIN;
}

/**
 * Build the full tenant URL for external links (email links, etc).
 * In 'domain' mode: https://{slug}.{baseDomain}
 * In 'session' mode: https://{baseDomain} (tenant comes from session, not URL)
 */
export function buildTenantUrl(slug: string, subPath: string = ''): string {
  const baseDomain = getBaseDomain();
  const suffix = subPath ? `/${subPath.replace(/^\//, '')}` : '';

  if (env.TENANT_RESOLUTION === 'domain') {
    return `https://${slug}.${baseDomain}${suffix}`;
  }
  // Session mode: just the base domain (tenant resolved from session)
  return `https://${baseDomain}${suffix}`;
}

/**
 * Build internal navigation path (used for in-app links).
 * Tenant is always resolved from session or domain — never from URL path.
 * So paths are flat: /{subPath}
 * When no subPath is given, returns /dashboard (the artist's home).
 */
export function buildTenantPath(_slug: string, subPath: string = ''): string {
  const suffix = subPath ? `/${subPath.replace(/^\//, '')}` : '';
  const base = suffix || '/dashboard';
  // When an admin is impersonating an artist, carry the ?as= param on every link
  const adminSlug = getAdminAsSlug();
  if (adminSlug) {
    return `${base}?as=${encodeURIComponent(adminSlug)}`;
  }
  return base;
}

/**
 * Extract tenant slug from the current hostname/URL.
 * Works with:
 * - Domain mode: artist.{baseDomain}
 * - Development: ?tenant= query param or artist.localhost
 * 
 * Note: Session mode no longer reads tenant from URL path.
 * The authenticated user's tenant is resolved from their session.
 */
export function getTenant(): TenantResult {
  const hostname = window.location.hostname;
  const baseDomain = getBaseDomain();

  // Universal: ?tenant= query param works on ALL domains (staging, preview, dev)
  const params = new URLSearchParams(window.location.search);
  const tenantParam = params.get('tenant');
  if (tenantParam && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(tenantParam)) {
    return { slug: tenantParam, isTenant: true };
  }

  // Development: handle localhost variants
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { slug: null, isTenant: false };
  }

  // Handle *.localhost for local dev (e.g., artist.localhost)
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.replace('.localhost', '');
    if (subdomain && !RESERVED_SUBDOMAINS.includes(subdomain)) {
      return { slug: subdomain, isTenant: true };
    }
    return { slug: null, isTenant: false };
  }

  // Domain mode: check if this is a subdomain of the base domain
  if (hostname.endsWith(`.${baseDomain}`)) {
    const subdomain = hostname.slice(0, -(baseDomain.length + 1));

    // Skip reserved subdomains
    if (RESERVED_SUBDOMAINS.includes(subdomain)) {
      return { slug: null, isTenant: false };
    }

    // Valid tenant slug
    if (subdomain && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/.test(subdomain)) {
      return { slug: subdomain, isTenant: true };
    }
  }

  // If hostname IS the baseDomain (root), no tenant
  if (hostname === baseDomain) {
    return { slug: null, isTenant: false };
  }

  return { slug: null, isTenant: false };
}

/**
 * React hook for tenant resolution.
 * Returns tenant info based on the current URL/domain.
 */
export function useTenant(): TenantResult {
  // Tenant is determined by domain — stable for the lifetime of the page
  return getTenant();
}
